import { Suspense } from 'react';
import { Route } from 'react-router-dom';

import { AuthGuard } from '../AuthGuard';
import { outOfShellRouteEntries } from './route-table';
import { RouteFallback } from './RouteFallback';

import type { OutOfShellRouteEntry } from './route-table';
import type { ReactElement } from 'react';

/**
 * The routes that render OUTSIDE the AuthGuard+AppShell layout — rendered from
 * the `outOfShellRouteEntries` table (`./route-table`, the single source of
 * truth) so this file and the router can never drift. Two families:
 *
 *   - **Public (unauthenticated)** — `/scan`, `/oauth/consent`, the C-12
 *     `/shared/impact/:token` shared report + `/shared/impact/unsubscribe/:token`.
 *     Driven by a token/URL, no session (`auth: false`).
 *   - **Authenticated interstitials outside the shell** — `/onboarding/…`,
 *     `/cli-auth`, `/checkout`, `/invite/accept` (`auth: true`). Behind AuthGuard
 *     but without the workspace chrome.
 *
 * Returned as a fragment of `<Route>`s and spread into the parent `<Routes>` —
 * React Router flattens fragment children when building its route tree. The
 * Suspense boundary here covers only these OUT-OF-SHELL lazy chunks (the in-shell
 * boundary lives around AppShell's Outlet).
 */
export function renderOutOfShellRoutes(): ReactElement {
  return <>{outOfShellRouteEntries.map(renderOutOfShellRoute)}</>;
}

function renderOutOfShellRoute(entry: OutOfShellRouteEntry): ReactElement {
  const Component = entry.component;
  // `entry.props` carries the static mode for pages that serve more than one
  // route (UnsubscribePage's `variant`). Spread as-is; the table types it as a
  // string record so nothing dynamic can leak into a route declaration.
  const element = (
    <Suspense fallback={<RouteFallback />}>
      <Component {...(entry.props ?? {})} />
    </Suspense>
  );
  return (
    <Route
      key={entry.path}
      path={entry.path}
      element={entry.auth ? <AuthGuard>{element}</AuthGuard> : element}
    />
  );
}
